// Mock REST server. Mirrors the endpoint contract the real backend should implement.
// Every handler returns the `data` part of a `{ success, data, message }` envelope.
import { db, clinic, landing, doctors, MOCK_OTP } from './db';
import { slotsFor, daySummary, nextAvailable } from './availability';
import { COUNTRY_CODES, UPCOMING_STATUSES, PAST_STATUSES } from '../../utils/constants';
import {
  toDateKey, addDays, fromDateKey, toDateTime, todayKey, nowLocalIso,
} from '../../utils/date';

class MockError extends Error {
  constructor(status, message, errors) {
    super(message);
    this.status = status;
    this.errors = errors;
  }
}

const fail = (status, message, errors) => {
  throw new MockError(status, message, errors);
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

/* ---------- serialisers ---------- */

const publicUser = (u) => {
  if (!u) return null;
  const { password, ...rest } = u;
  return rest;
};

const doctorCard = (d) => {
  const [next] = nextAvailable(d, 1);
  return {
    id: d.id,
    name: d.name,
    specialty: d.specialty,
    qualifications: d.qualifications,
    languages: d.languages,
    photoUrl: d.photoUrl,
    featured: d.featured,
    featuredRank: d.featuredRank || null,
    consultationFee: d.consultationFee,
    nextAvailable: next || null,
  };
};

const doctorDetail = (d) => ({
  ...doctorCard(d),
  experienceYears: d.experienceYears,
  bio: d.bio,
  focusAreas: d.focusAreas,
  consultationHours: d.consultationHours,
  location: clinic.address,
});

const findDoctor = (id) => doctors.find((d) => d.id === id) || fail(404, 'Doctor not found');

const apptOut = (a) => {
  const d = doctors.find((x) => x.id === a.doctorId);
  return {
    ...a,
    doctor: d ? { id: d.id, name: d.name, specialty: d.specialty, photoUrl: d.photoUrl, languages: d.languages } : null,
    fee: d?.consultationFee ?? null,
    location: 'In-clinic',
  };
};


const byDateTime = (a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`);

/* ---------- auth helpers ---------- */

const tokenFor = (u) => `mock.${u.id}.${Date.now().toString(36)}`;
const userFromToken = (token) => {
  const id = token?.split('.')[1];
  return db.state.users.find((u) => u.id === id) || null;
};
const session = (u) => ({ token: tokenFor(u), user: publicUser(u) });
const findByPhone = (countryCode, phone) =>
  db.state.users.find((u) => u.countryCode === countryCode && u.phone === phone);

const pushNotification = (userId, n) => {
  db.state.notifications.unshift({
    id: db.nextId('n'),
    userId,
    read: false,
    createdAt: nowLocalIso(),
    ...n,
  });
};

/* ---------- routes ---------- */

const routes = [];
const route = (method, pattern, handler, { auth = false } = {}) => {
  const keys = [];
  const regex = new RegExp(
    `^${pattern.replace(/:([a-zA-Z]+)/g, (_, k) => {
      keys.push(k);
      return '([^/]+)';
    })}$`,
  );
  routes.push({ method, regex, keys, handler, auth });
};

// General / CMS / clinic
route('GET', '/clinic', () => clinic);
route('GET', '/cms/landing', () => landing);
route('GET', '/general/country-codes', () => COUNTRY_CODES);

// Auth — demo mode: any mobile number, password and 6-digit code works.
// Unknown numbers sign in to the seeded demo patient so the demo always has data.
const demoAccount = (cc, phone) => findByPhone(cc, phone) || db.state.users[0];
const validCode = (code) => /^\d{6}$/.test(code || '');

route('POST', '/auth/login', ({ data }) => {
  if (!data.phone || !data.password) fail(401, 'Enter your mobile number and password.');
  return session(demoAccount(data.countryCode, data.phone));
});

route('POST', '/auth/otp/request', ({ data }) => {
  const exists = !!findByPhone(data.countryCode, data.phone);
  if (data.purpose === 'register' && exists) fail(409, 'This mobile number already has an account. Please log in.');
  return { expiresIn: 300, resendIn: 30, hint: `Demo code: ${MOCK_OTP}` };
});

route('POST', '/auth/otp/verify', ({ data }) => {
  if (!validCode(data.code)) fail(422, 'The code you entered is incorrect.');
  if (data.purpose === 'login') return session(demoAccount(data.countryCode, data.phone));
  if (data.purpose === 'reset') return { resetToken: `reset.${data.countryCode}.${data.phone}` };
  return { verified: true };
});

route('POST', '/auth/register', ({ data }) => {
  if (!validCode(data.code)) fail(422, 'The code you entered is incorrect.');
  if (findByPhone(data.countryCode, data.phone)) fail(409, 'This mobile number already has an account.');
  const u = {
    id: db.nextId('u'),
    name: data.name,
    preferredName: data.name.split(' ')[0],
    countryCode: data.countryCode,
    phone: data.phone,
    password: data.password,
    email: data.email || '',
    icNumber: data.icNumber,
    dateOfBirth: data.dateOfBirth || '',
    gender: data.gender || '',
    smsReminders: true,
    createdAt: todayKey(),
  };
  db.state.users.push(u);
  db.save();
  return session(u);
});

route('POST', '/auth/password/reset', ({ data }) => {
  const [, cc, phone] = (data.resetToken || '').split('.');
  const u = demoAccount(cc, phone);
  u.password = data.password;
  db.save();
  return { reset: true };
});

route('POST', '/auth/logout', () => ({ ok: true }), { auth: true });
route('GET', '/auth/me', ({ user }) => publicUser(user), { auth: true });

// Doctors
route('GET', '/doctors/specialties', () => [...new Set(doctors.map((d) => d.specialty))]);

route('GET', '/doctors', ({ params }) => {
  const q = (params.q || '').trim().toLowerCase();
  return doctors
    .filter((d) => !params.specialty || d.specialty === params.specialty)
    .filter((d) => !params.featured || d.featured)
    .filter(
      (d) => !q
        || [d.name, d.specialty, ...d.languages, ...d.focusAreas].some((v) => v.toLowerCase().includes(q)),
    )
    .map(doctorCard);
});

route('GET', '/doctors/:id', ({ id }) => doctorDetail(findDoctor(id)));

route('GET', '/doctors/:id/availability', ({ id, params }) => {
  const d = findDoctor(id);
  const from = fromDateKey(params.from);
  const to = fromDateKey(params.to);
  const out = [];
  for (let day = from; day <= to; day = addDays(day, 1)) {
    out.push(daySummary(d, toDateKey(day), { excludeAppointmentId: params.exclude }));
  }
  return out;
});

route('GET', '/doctors/:id/earliest', ({ id, params }) => nextAvailable(findDoctor(id), Number(params.limit) || 3));

route('GET', '/doctors/:id/slots', ({ id, params }) =>
  slotsFor(findDoctor(id), params.date, { excludeAppointmentId: params.exclude }));

// Appointments
const ownAppt = (user, id) =>
  db.state.appointments.find((a) => a.id === id && a.userId === user.id) || fail(404, 'Appointment not found');

route('GET', '/appointments', ({ user, params }) => {
  const mine = db.state.appointments.filter((a) => a.userId === user.id);
  let list = mine;
  if (params.status === 'upcoming') list = mine.filter((a) => UPCOMING_STATUSES.includes(a.status)).sort(byDateTime);
  else if (params.status === 'past') list = mine.filter((a) => PAST_STATUSES.includes(a.status)).sort(byDateTime).reverse();
  else if (params.status === 'cancelled') list = mine.filter((a) => a.status === 'cancelled').sort(byDateTime).reverse();
  return list.map(apptOut);
}, { auth: true });

route('GET', '/appointments/summary', ({ user }) => {
  const mine = db.state.appointments.filter((a) => a.userId === user.id);
  return {
    upcoming: mine.filter((a) => UPCOMING_STATUSES.includes(a.status)).length,
    past: mine.filter((a) => PAST_STATUSES.includes(a.status)).length,
    cancelled: mine.filter((a) => a.status === 'cancelled').length,
  };
}, { auth: true });

route('GET', '/appointments/:id', ({ user, id }) => apptOut(ownAppt(user, id)), { auth: true });

const assertSlot = (doctor, date, time, excludeAppointmentId) => {
  const slot = slotsFor(doctor, date, { excludeAppointmentId }).find((s) => s.time === time);
  if (!slot || !slot.available) fail(409, 'Sorry, that time was just taken. Please choose another slot.');
};

route('POST', '/appointments', ({ user, data }) => {
  const d = findDoctor(data.doctorId);
  assertSlot(d, data.date, data.time);
  const a = {
    id: db.nextId('a'),
    userId: user.id,
    doctorId: d.id,
    date: data.date,
    time: data.time,
    duration: clinic.slotMinutes,
    status: clinic.autoConfirm ? 'confirmed' : 'pending',
    visitType: null,
    reason: (data.reason || '').trim(),
    createdAt: nowLocalIso(),
  };
  db.state.appointments.push(a);
  pushNotification(user.id, {
    type: 'request',
    title: clinic.autoConfirm ? 'Appointment confirmed' : 'Booking request received',
    body: `Your appointment with ${d.name} is ${clinic.autoConfirm ? 'confirmed' : 'awaiting clinic confirmation'}.`,
    appointmentId: a.id,
  });
  db.save();
  return apptOut(a);
}, { auth: true });

const assertChangeable = (a) => {
  if (!UPCOMING_STATUSES.includes(a.status)) fail(409, 'This appointment can no longer be changed.');
  const hoursLeft = (toDateTime(a.date, a.time) - new Date()) / 36e5;
  if (hoursLeft < clinic.cancellationCutoffHours) {
    fail(409, `Changes must be made at least ${clinic.cancellationCutoffHours} hours before your visit. Please call the clinic.`);
  }
};

route('POST', '/appointments/:id/cancel', ({ user, id, data }) => {
  const a = ownAppt(user, id);
  assertChangeable(a);
  a.status = 'cancelled';
  a.cancelReason = data.reason || '';
  a.cancelledAt = nowLocalIso();
  db.save();
  return apptOut(a);
}, { auth: true });

route('POST', '/appointments/:id/reschedule', ({ user, id, data }) => {
  const a = ownAppt(user, id);
  assertChangeable(a);
  const d = findDoctor(a.doctorId);
  assertSlot(d, data.date, data.time, a.id);
  a.date = data.date;
  a.time = data.time;
  a.status = clinic.autoConfirm ? 'confirmed' : 'pending';
  if (data.reason !== undefined) a.reason = data.reason;
  pushNotification(user.id, {
    type: 'request',
    title: 'Reschedule request received',
    body: `Your new time with ${d.name} is awaiting clinic confirmation.`,
    appointmentId: a.id,
  });
  db.save();
  return apptOut(a);
}, { auth: true });

// Profile
route('GET', '/profile', ({ user }) => publicUser(user), { auth: true });

route('PUT', '/profile', ({ user, data }) => {
  const allowed = ['name', 'preferredName', 'email', 'icNumber', 'dateOfBirth', 'gender', 'countryCode', 'phone', 'smsReminders'];
  if (data.phone && data.countryCode) {
    const other = findByPhone(data.countryCode, data.phone);
    if (other && other.id !== user.id) fail(409, 'This mobile number is used by another account.');
  }
  allowed.forEach((k) => {
    if (data[k] !== undefined) user[k] = data[k];
  });
  db.save();
  return publicUser(user);
}, { auth: true });

route('POST', '/profile/password', ({ user, data }) => {
  if (user.password !== data.currentPassword) fail(422, 'Current password is incorrect.', { currentPassword: 'Current password is incorrect' });
  user.password = data.newPassword;
  db.save();
  return { updated: true };
}, { auth: true });

// Notifications
route('GET', '/notifications', ({ user }) =>
  db.state.notifications.filter((n) => n.userId === user.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), { auth: true });

route('POST', '/notifications/read-all', ({ user }) => {
  db.state.notifications.forEach((n) => {
    if (n.userId === user.id) n.read = true;
  });
  db.save();
  return { ok: true };
}, { auth: true });

route('POST', '/notifications/:id/read', ({ user, id }) => {
  const n = db.state.notifications.find((x) => x.id === id && x.userId === user.id);
  if (n) n.read = true;
  db.save();
  return { ok: true };
}, { auth: true });

/* ---------- dispatcher ---------- */

export async function mockRequest({ method, url, params = {}, data = {}, token }) {
  await delay(180 + Math.random() * 220);
  const path = url.split('?')[0];
  const match = routes.find((r) => r.method === method && r.regex.test(path));
  if (!match) return { status: 404, body: { success: false, message: `Mock route not found: ${method} ${path}` } };

  const values = path.match(match.regex).slice(1);
  const ctx = { params, data: data || {} };
  match.keys.forEach((k, i) => {
    ctx[k] = decodeURIComponent(values[i]);
  });

  try {
    if (match.auth) {
      ctx.user = userFromToken(token);
      if (!ctx.user) fail(401, 'Your session has expired. Please log in again.');
    }
    const result = await match.handler(ctx);
    return { status: 200, body: { success: true, data: result, message: 'OK' } };
  } catch (e) {
    if (e instanceof MockError) {
      return { status: e.status, body: { success: false, message: e.message, errors: e.errors } };
    }
    // eslint-disable-next-line no-console
    console.error('[mock api]', e);
    return { status: 500, body: { success: false, message: 'Something went wrong. Please try again.' } };
  }
}
